import { useCallback, useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useFormStepContext } from '../FormStepContext'
import { isDefaultSubtaskTitle } from '../utils'
import { mergeAiSubtask } from './mergeAiSubtask'
import { AIStepType } from '../../AITaskCreation/AIFormStepContext'
import { AI_SUBTASK_DRAFT_SLICE, buildAiSubtaskDraft } from '../../AITaskCreation/hooks/useAiSubtaskDraft'

/** Stable-card session: source RHF v5 reads stay outside nested AI forms; leaf writes retain registrations. */
export function useVmAiSession(index: number, onDone: () => void) {
  const parent = useFormStepContext()
  const { getValues, setValue } = useFormContext()

  const [formData, setFormData] = useState<any>(() => {
    const source: any = getValues({ nest: true }).sub_tasks?.[index] || {}

    return {
      AI_PROMPT: { prompt: source.ai_prompt || '', reference_images: source.reference_images || [] },
      [AI_SUBTASK_DRAFT_SLICE]: {
        title: '',
        sub_tasks: [{ ...source, title: isDefaultSubtaskTitle(source.title) ? undefined : source.title }],
      },
    }
  })
  const [steps, setSteps] = useState<string[]>([AIStepType.AI_PROMPT])
  const [submit, setSubmit] = useState<(() => void) | null>(null)

  const dataRef = useRef(formData)

  const updateFormData = useCallback((key: string, value: any) => {
    dataRef.current = { ...dataRef.current, [key]: value }
    if (key === 'AI_PROMPT' && value.reference_images) {
      const draft = dataRef.current[AI_SUBTASK_DRAFT_SLICE]
      dataRef.current = {
        ...dataRef.current,
        [AI_SUBTASK_DRAFT_SLICE]: {
          ...draft,
          sub_tasks: draft.sub_tasks.map((subtask: any) => ({
            ...subtask,
            content: { ...subtask.content, images: value.reference_images },
          })),
        },
      }
    }
    setFormData(dataRef.current)
  }, [])

  const getFormData = useCallback(() => dataRef.current, [])

  const onGenerated = useCallback(
    (result, images) => {
      updateFormData(AI_SUBTASK_DRAFT_SLICE, buildAiSubtaskDraft(result, images))
    },
    [updateFormData]
  )

  const syncDraft = useCallback(
    (data: any) => {
      dataRef.current = { ...dataRef.current, [AI_SUBTASK_DRAFT_SLICE]: data }
      const path = `sub_tasks[${index}]`
      const source = getValues({ nest: true }).sub_tasks?.[index]

      if (!source) return

      const generated = data.sub_tasks?.[0] || {}
      const merged = {
        ...mergeAiSubtask(source, generated, dataRef.current.AI_PROMPT),
        title: generated.title ?? source.title,
      }

      for (const key of ['title', 'is_ai_enhanced', 'prompt_id', 'reference_images', 'ai_prompt'])
        setValue(`${path}.${key}`, merged[key])
      setValue(`${path}.content.rich_text`, merged.content.rich_text)
      setValue(`${path}.content.images`, merged.content.images)
    },
    [getValues, index, setValue]
  )

  const complete = (data: any) => {
    syncDraft(data)
    onDone()
  }

  useEffect(() => {
    syncDraft(dataRef.current[AI_SUBTASK_DRAFT_SLICE])
  }, [formData, syncDraft])

  return {
    syncDraft,
    onGenerated,
    complete,
    context: {
      ...parent,
      formData,
      getFormData,
      updateFormData,
      stepStack: steps,
      setStepStack: setSteps,
      currentFormSubmit: submit,
      setCurrentFormSubmit: setSubmit,
      sumbitStep: () => submit?.(),
      goToPreviousStep: () => setSteps((previous) => (previous.length > 1 ? previous.slice(0, -1) : previous)),
    },
  }
}
